import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useWriteContract, usePublicClient } from 'wagmi';
import { formatUsd, type Chain } from '@ithildin/shared';
import type { Hex } from 'viem';

import { api, ApiError } from '../lib/api';
import { config } from '../lib/config';
import { draft as draftStore, fundedEscrow } from '../lib/drafts';
import {
  buildSolanaFundTx,
  deriveSolanaEscrow,
  ESCROW_ABI,
  ERC20_APPROVE_ABI,
  freshNonce,
  hashBody,
} from '../lib/escrow';
import { shortAddress } from '../lib/format';
import { ReplyRecord } from '../components/ReplyRecord';

const WINDOW_OPTIONS = [
  { hours: 24, label: '24 hours' },
  { hours: 48, label: '2 days' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '1 week' },
  { hours: 336, label: '2 weeks' },
  { hours: 720, label: '30 days' },
];

export function Compose() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const saved = useMemo(() => draftStore.load(), []);
  const [to, setTo] = useState(params.get('to') ?? saved?.to ?? '');
  const [body, setBody] = useState(saved?.body ?? '');
  const [windowHours, setWindowHours] = useState(saved?.windowHours ?? 48);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stranded, setStranded] = useState(() => fundedEscrow.load());

  const solanaWallet = useWallet();
  const { connection } = useConnection();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  // Persist on every change: the wallet popup can take the tab away at any point.
  useEffect(() => {
    if (to || body) draftStore.save({ to, body, windowHours });
  }, [to, body, windowHours]);

  // The chain is never asked for — an address or name already determines it.
  const lookup = useQuery({
    queryKey: ['resolve', to],
    queryFn: () => api.resolve(to),
    enabled: to.trim().length >= 4,
    retry: false,
  });

  const resolved = lookup.data ?? null;
  const profile = resolved?.profile ?? null;
  const priceMicros = profile?.inboxPriceMicros ?? '0';
  const isPaid = BigInt(priceMicros) > 0n;
  const minWindow = profile?.replyWindowHours ?? 24;

  useEffect(() => {
    if (minWindow > windowHours) setWindowHours(minWindow);
  }, [minWindow]);

  async function deliver(escrow: {
    amountMicros: string;
    replyWindowHours: number;
    fundingTxSignature: string;
    vaultAddress: string;
  } | undefined, toChain: Chain, toAddress: string, text: string) {
    const result = await api.send({ toChain, toAddress, body: text, escrow });
    draftStore.clear();
    fundedEscrow.clear();
    navigate(`/thread/${result.threadId}`);
  }

  /** Retry delivery for a payment that already confirmed on-chain. */
  async function retryStranded() {
    if (!stranded) return;
    setBusy(true);
    setError(null);
    setStatus('Delivering the message…');
    try {
      await deliver(
        {
          amountMicros: stranded.amountMicros,
          replyWindowHours: stranded.replyWindowHours,
          fundingTxSignature: stranded.fundingTxSignature,
          vaultAddress: stranded.vaultAddress,
        },
        stranded.toChain,
        stranded.toAddress,
        stranded.body,
      );
    } catch (err) {
      // 409 means the escrow was already recorded — the original send did land
      // and only its response was lost, so there is nothing left to recover.
      if (err instanceof ApiError && err.status === 409) {
        fundedEscrow.clear();
        setStranded(null);
        navigate('/inbox');
        return;
      }
      setError(err instanceof Error ? err.message : 'could not deliver');
    } finally {
      setBusy(false);
      setStatus(null);
    }
  }

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      const text = body.trim();
      if (!text) throw new Error('write something first');
      if (!resolved || !profile) throw new Error('enter a wallet address or .eth / .sol name');
      if (profile.paused && isPaid) throw new Error('this inbox is not taking paid messages right now');
      if (windowHours < minWindow) {
        throw new Error(`this inbox asks for at least ${minWindow} hours to reply`);
      }

      const chain = resolved.chain as Chain;
      let escrow: Parameters<typeof deliver>[0];

      if (isPaid) {
        const messageHash = hashBody(text);
        const nonce = freshNonce();
        const amount = BigInt(priceMicros);

        if (chain === 'solana') {
          setStatus('Funding the escrow on Solana…');
          if (!solanaWallet.publicKey) throw new Error('connect your Solana wallet');

          const tx = await buildSolanaFundTx({
            connection,
            programId: config.solana.programId,
            usdcMint: config.solana.usdcMint,
            sender: solanaWallet.publicKey,
            recipient: new PublicKey(resolved.address),
            amountMicros: amount,
            replyWindowSecs: windowHours * 3600,
            messageHash,
            nonce,
          });
          const signature = await solanaWallet.sendTransaction(tx, connection);
          setStatus('Waiting for confirmation…');
          await connection.confirmTransaction(signature, 'confirmed');

          const { vault } = deriveSolanaEscrow({
            programId: config.solana.programId,
            sender: solanaWallet.publicKey,
            recipient: new PublicKey(resolved.address),
            nonce,
          });
          escrow = {
            amountMicros: amount.toString(),
            replyWindowHours: windowHours,
            fundingTxSignature: signature,
            vaultAddress: vault.toBase58(),
          };
        } else {
          setStatus('Approving USDC…');
          const approveHash = await writeContractAsync({
            address: config.ethereum.usdcAddress,
            abi: ERC20_APPROVE_ABI,
            functionName: 'approve',
            args: [config.ethereum.escrowAddress, amount],
          });
          await publicClient?.waitForTransactionReceipt({ hash: approveHash });

          setStatus('Funding the escrow on Ethereum…');
          const fundHash = await writeContractAsync({
            address: config.ethereum.escrowAddress,
            abi: ESCROW_ABI,
            functionName: 'fund',
            args: [resolved.address as Hex, nonce, amount, BigInt(windowHours * 3600), messageHash],
          });
          await publicClient?.waitForTransactionReceipt({ hash: fundHash });

          escrow = {
            amountMicros: amount.toString(),
            replyWindowHours: windowHours,
            fundingTxSignature: fundHash,
            vaultAddress: config.ethereum.escrowAddress,
          };
        }

        // Recorded before delivery is attempted. From here the money is
        // committed on-chain, so if the request fails the sender must be able
        // to find it again rather than wait out a silent refund.
        fundedEscrow.save({
          toChain: chain,
          toAddress: resolved.address,
          body: text,
          fundedAt: new Date().toISOString(),
          ...escrow,
        });
        setStranded(fundedEscrow.load());
      }

      setStatus('Delivering the message…');
      await deliver(escrow, chain, resolved.address, text);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not send');
    } finally {
      setBusy(false);
      setStatus(null);
    }
  }

  return (
    <div className="page page-narrow">
      <div className="page-head"><h2>New message</h2></div>

      {stranded && (
        <div className="recovery">
          <p>
            You funded {formatUsd(stranded.amountMicros)} for a message to{' '}
            <span className="addr">{shortAddress(stranded.toAddress, 5, 5)}</span> that never
            went through. The payment is on-chain and safe — send it, or leave it and the
            escrow returns to you when the window closes.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void retryStranded()}>
              Deliver it now
            </button>
            <button
              className="btn btn-sm btn-quiet"
              onClick={() => { fundedEscrow.clear(); setStranded(null); }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 22 }}>
        <label className="label" htmlFor="to">Recipient</label>
        <input
          id="to"
          className="field mono"
          value={to}
          spellCheck={false}
          placeholder="vitalik.eth, toly.sol, or a wallet address"
          onChange={(e) => setTo(e.target.value)}
        />

        {to.trim().length >= 4 && lookup.isLoading && (
          <p className="hint" style={{ marginTop: 8 }}>Looking that up…</p>
        )}
        {lookup.isError && (
          <p className="hint hint-warn" style={{ marginTop: 8 }}>
            No wallet found for that name or address.
          </p>
        )}

        {resolved && profile && (
          <div style={{ marginTop: 12 }}>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <Link to={`/w/${resolved.name ?? resolved.address}`} className="name">
                {profile.displayName ?? resolved.name ?? shortAddress(resolved.address, 6, 6)}
              </Link>
              <span className="addr">{shortAddress(resolved.address, 5, 5)}</span>
              <span className="addr">{resolved.chain}</span>
            </div>

            {profile.paused && isPaid ? (
              <p className="hint hint-warn" style={{ marginTop: 10 }}>
                Not taking paid messages right now.
              </p>
            ) : isPaid ? (
              <p className="hint" style={{ marginTop: 10 }}>
                {formatUsd(priceMicros)} to open this thread, held in escrow and returned in
                full if they don't reply.
              </p>
            ) : (
              <p className="hint" style={{ marginTop: 10 }}>Open inbox — no fee to send.</p>
            )}

            {isPaid && <ReplyRecord record={profile.record} windowHours={profile.replyWindowHours} />}
          </div>
        )}

        <label className="label" style={{ marginTop: 20 }} htmlFor="body">Message</label>
        <textarea
          id="body"
          className="field"
          value={body}
          maxLength={4000}
          placeholder="Say what you want and why it is worth their time…"
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="hint" style={{ marginTop: 6 }}>{body.length} / 4000</p>

        {isPaid && (
          <>
            <label className="label" style={{ marginTop: 18 }}>
              How long do they have to reply?
            </label>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {WINDOW_OPTIONS.map((w) => (
                <button
                  key={w.hours}
                  className={`btn btn-sm ${windowHours === w.hours ? '' : 'btn-quiet'}`}
                  disabled={w.hours < minWindow}
                  onClick={() => setWindowHours(w.hours)}
                >
                  {w.label}
                </button>
              ))}
            </div>

            <div className="cost-preview" style={{ marginTop: 16, flexDirection: 'column', gap: 6 }}>
              <div className="cost-row">
                <span className="hint">Escrowed fee</span>
                <span className="mono">{formatUsd(priceMicros)}</span>
              </div>
              <div className="cost-row">
                <span className="hint">Network fee</span>
                <span className="mono hint">
                  {resolved?.chain === 'solana'
                    ? 'under $0.01'
                    : 'gas, quoted by your wallet'}
                </span>
              </div>
              <div className="cost-row cost-total">
                <span>You get back if ignored</span>
                <span className="mono">{formatUsd(priceMicros)}</span>
              </div>
            </div>
          </>
        )}

        <div className="row" style={{ marginTop: 20, gap: 12 }}>
          {status && <span className="hint">{status}</span>}
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={busy || !resolved || !body.trim() || (profile?.paused && isPaid)}
            onClick={() => void submit()}
          >
            {busy ? 'Working…' : isPaid ? `Pay ${formatUsd(priceMicros)} and send` : 'Send'}
          </button>
        </div>

        {error && <p className="hint hint-warn" style={{ marginTop: 12, marginBottom: 0 }}>{error}</p>}
      </div>
    </div>
  );
}
